import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import { environment } from 'environments/environment';
import { RangeOfDate } from '@olv/ui-shared/services/date-picker.service';

type StyleDropdown = 'standard' | 'custom';

type DatePickerMode = 'dropdown' | 'menu';
type DateSelectionMode = 'range' | 'single';

export interface DateFilterState {
  key: string;
  label: string;
  value?: string;
  date?: Date | null;
  start?: Date | null;
  end?: Date | null;
}

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
  ],
  templateUrl: './date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent implements OnInit {
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild(MatCalendar) calendar?: MatCalendar<Date>;
  @ViewChild('dropdownCalendar') dropdownCalendar?: ElementRef;
  @ViewChild('dropdownInput') dropdownInput?: ElementRef;

  showCalendar = false;
  selectedChip = '';
  maxDate = this.startOfDay(new Date());
  minDate = new Date(environment.DATE_PICKER.MIN_DATE);
  rangeErrorMessage = '';

  rangeStart: Date | null = null;
  rangeEnd: Date | null = null;
  private committedRangeStart: Date | null = null;
  private committedRangeEnd: Date | null = null;
  @Input() reduceMaxDate = 0;
  @Input() selectedPeriod = 'Month';
  @Input() datePickerMode: DatePickerMode = 'menu';
  @Input() selectionMode: DateSelectionMode = 'range';
  @Input() dateKey = '';
  @Input() dateLabel = '';
  @Input() isDisabled = false;
  @Input() showAllTime = false;
  @Input() showToday = false;
  @Input() allowSingleInRange = false;
  @Input() showActions = false;
  @Output() rangeChange = new EventEmitter<RangeOfDate>();
  @Output() dateChange = new EventEmitter<Date>();
  //Need to add PresetPeriod type for presetSelected
  @Output() presetSelected = new EventEmitter<string>();

  @Input() styleDropdown: StyleDropdown = 'standard';
  @Input()
  set selectedDate(value: Date | null) {
    this._selectedDate = value ? this.startOfDay(value) : null;
    if (this.selectionMode === 'single') {
      this.rangeStart = this._selectedDate;
      this.rangeEnd = null;
      this.updateDropdownValue();
      this.refreshCalendar();
    }
  }

  get selectedDate(): Date | null {
    return this._selectedDate;
  }

  @Input()
  set dateFilter(value: DateFilterState) {
    this._dateFilter = value;
    this.getSelectedDropdownCalendar();
  }

  get dateFilter(): DateFilterState {
    return this._dateFilter;
  }

  get effectiveReduceMaxDate(): number {
    if (this.resolvedDateKey === 'dateLedger') {
      return 0;
    }
    return this.reduceMaxDate;
  }

  get resolvedDateKey(): string {
    return this.selectionMode === 'single' ? this.dateKey : this.dateFilter.key;
  }

  get resolvedDateLabel(): string {
    return this.selectionMode === 'single' ? this.dateLabel : this.dateFilter.label;
  }

  get calendarSelectedDate(): Date | null {
    return this.selectionMode === 'single' ? this.selectedDate : this.rangeStart;
  }
  dropdownValue = 'Select date';
  dropdownCalendarOpened = false;

  private _dateFilter: DateFilterState = { key: '', label: '' };
  private _selectedDate: Date | null = null;
  private maxRangeMonths = environment.DATE_PICKER.MAX_RANGE_MONTHS;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.datePickerMode !== 'dropdown' || !this.dropdownCalendarOpened) return;

    const target = event.target as HTMLElement;
    const clickedInside =
      this.dropdownCalendar?.nativeElement.contains(target) || this.dropdownInput?.nativeElement.contains(target);

    if (!clickedInside) {
      this.finalizeSingleSelectionOnClose();
      this.dropdownCalendarOpened = false;
    }
  }

  private finalizeSingleSelectionOnClose() {
    if (this.selectionMode !== 'range' || !this.allowSingleInRange || !this.rangeStart || this.rangeEnd) return;
    this.rangeEnd = this.endOfDay(this.rangeStart);
    this.updateDropdownValue();
    this.rangeChange.emit({ start: this.rangeStart, end: this.rangeEnd });
  }

  ngOnInit() {
    if (this.datePickerMode === 'dropdown') {
      this.getSelectedDropdownCalendar();
      this.updateDropdownValue();
    }

    if (this.effectiveReduceMaxDate > 0) {
      this.reduceMaxDateByDays(this.effectiveReduceMaxDate);
    }
  }

  reduceMaxDateByDays(days: number) {
    const today = this.startOfDay(new Date());
    today.setDate(today.getDate() - days);
    this.maxDate = today;
  }

  toggleDropdownCalendar() {
    this.dropdownCalendarOpened = !this.dropdownCalendarOpened;
    if (this.dropdownCalendarOpened) {
      this.committedRangeStart = this.rangeStart;
      this.committedRangeEnd = this.rangeEnd;
    }
  }

  triggerDatePicker(): void {
    this.selectedChip = this.selectedPeriod;
    if (this.selectedChip !== 'Custom') {
      this.showCalendar = false;
    }
  }

  onChipClick(chip: string | 'Custom') {
    this.selectedChip = chip;
    this.rangeStart = null;
    this.rangeEnd = null;
    this.rangeErrorMessage = '';
    this.refreshCalendar();

    if (chip === 'Custom') {
      this.showCalendar = true;
      if (this.calendar) {
        this.calendar.activeDate = new Date();
      }
      return;
    }

    if (chip === 'All time') {
      this.selectedPeriod = chip;
      this.presetSelected.emit(chip);
      this.menuTrigger.closeMenu();
      this.showCalendar = false;
      return;
    } else {
      this.presetSelected.emit(chip);
      this.menuTrigger.closeMenu();
    }

    this.showCalendar = false;
  }

  onCalendarDateSelected(date: Date | null) {
    if (!date) return;
    const clicked = this.startOfDay(date);

    if (this.selectionMode === 'single') {
      this._selectedDate = clicked;
      this.rangeStart = clicked;
      this.rangeEnd = null;
      this.rangeErrorMessage = '';
      this.updateDropdownValue();

      if (this.datePickerMode === 'dropdown') {
        this.dropdownCalendarOpened = false;
      }

      this.dateChange.emit(clicked);
      this.refreshCalendar();
      return;
    }

    if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
      this.rangeStart = clicked;
      this.rangeEnd = null;
      this.rangeErrorMessage = '';

      if (this.datePickerMode === 'dropdown' && this.allowSingleInRange) {
        this.updateDropdownValue();
        if (!this.showActions) {
          this.rangeChange.emit({ start: this.rangeStart, end: this.endOfDay(this.rangeStart) });
        }
      }
    } else {
      const start = clicked < this.rangeStart ? clicked : this.rangeStart;
      const end = clicked < this.rangeStart ? this.rangeStart : clicked;
      if (!this.isRangeValid(start, end)) {
        this.rangeErrorMessage = 'Date range cannot exceed 36 months.';
        return;
      }

      this.rangeStart = start;
      this.rangeEnd = this.endOfDay(end);
      this.rangeErrorMessage = '';
    }

    if (this.datePickerMode === 'dropdown' && this.rangeStart && this.rangeEnd) {
      this.updateDropdownValue();
      this.dropdownCalendarOpened = false;
      this.rangeChange.emit({ start: this.rangeStart, end: this.rangeEnd });
    }

    this.refreshCalendar();
  }

  private getSelectedDropdownCalendar() {
    if (this.selectionMode === 'single') {
      this.rangeStart = this.selectedDate;
      this.rangeEnd = null;
    } else if (this.dateFilter.start && this.dateFilter.end) {
      this.rangeStart = this.dateFilter.start;
      this.rangeEnd = this.dateFilter.end;
    } else {
      this.rangeStart = null;
      this.rangeEnd = null;
    }

    this.updateDropdownValue();
    this.refreshCalendar();
  }

  private updateDropdownValue(): void {
    if (this.rangeStart && this.rangeEnd) {
      const sameDay = this.startOfDay(this.rangeStart).getTime() === this.startOfDay(this.rangeEnd).getTime();
      this.dropdownValue = sameDay
        ? this.formatDateValue(this.rangeStart)
        : `${this.formatDateValue(this.rangeStart)} - ${this.formatDateValue(this.rangeEnd)}`;
    } else if (this.rangeStart && (this.selectionMode === 'single' || this.allowSingleInRange)) {
      this.dropdownValue = this.formatDateValue(this.rangeStart);
    } else {
      this.dropdownValue = this.selectionMode === 'single' ? 'Select date' : this.dateFilter.value || 'Select date';
    }
  }

  private formatDateValue(date: Date): string {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private isRangeValid(start: Date, end: Date): boolean {
    const monthsDiff = this.getMonthsDifference(start, end);
    return monthsDiff <= this.maxRangeMonths;
  }

  private getMonthsDifference(start: Date, end: Date): number {
    const yearDiff = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();
    const dayDiff = end.getDate() - start.getDate();

    let totalMonths = yearDiff * 12 + monthDiff;

    if (dayDiff > 0) {
      const extraMonths = Math.floor(dayDiff / 30);
      totalMonths += extraMonths;
    }

    return totalMonths;
  }

  private refreshCalendar() {
    this.calendar?.updateTodaysDate();
  }

  confirmDropdownSelection() {
    if (!this.rangeStart) return;
    if (!this.rangeEnd && !this.allowSingleInRange) return;
    const end = this.rangeEnd ?? this.endOfDay(this.rangeStart);
    this.updateDropdownValue();
    this.rangeChange.emit({ start: this.rangeStart, end });
    this.dropdownCalendarOpened = false;
  }

  closeDropdownCalendar() {
    if (this.showActions) {
      this.revertDropdownSelection();
    }
    this.dropdownCalendarOpened = false;
  }

  private revertDropdownSelection() {
    this.rangeStart = this.committedRangeStart;
    this.rangeEnd = this.committedRangeEnd;
    this.rangeErrorMessage = '';
    this.updateDropdownValue();
    this.refreshCalendar();
  }

  emitDateRange() {
    if (!this.rangeStart) return;

    let end = this.rangeEnd;
    if (!end) {
      if (!this.allowSingleInRange) return;
      end = this.endOfDay(this.rangeStart);
    }

    const payload: RangeOfDate = { start: this.rangeStart, end };
    this.rangeChange.emit(payload);
    this.menuTrigger.closeMenu();
  }

  private startOfDay(d: Date) {
    const n = new Date(d);
    n.setHours(0, 0, 0, 0);
    return n;
  }

  private endOfDay(d: Date) {
    const n = new Date(d);
    n.setHours(23, 59, 59, 999);
    return n;
  }

  dateClass = (date: Date) => {
    if (!this.rangeStart) return '';
    const day = this.startOfDay(date).getTime();
    const start = this.startOfDay(this.rangeStart).getTime();
    const end = this.rangeEnd ? this.startOfDay(this.rangeEnd).getTime() : NaN;

    if (this.selectionMode === 'single') {
      return day === start ? 'range-start' : '';
    }

    if (this.rangeStart && !this.rangeEnd) {
      return day === start ? 'range-start' : '';
    }

    if (this.rangeStart && this.rangeEnd) {
      if (day === start) return 'range-start';
      if (day === end) return 'range-end';
      if (day > start && day < end) return 'in-range';
    }
    return '';
  };
}
